export type CsvCell = string | number | boolean | null | undefined;

type CsvExportOptions<TRow> = {
  columns: Array<{
    header: string;
    value: (row: TRow) => CsvCell;
  }>;
  rows: readonly TRow[];
};

const FORMULA_PREFIX_PATTERN = /^[=+\-@]/;

function sanitizeCsvCell(value: CsvCell): string {
  const raw = value === null || value === undefined ? "" : String(value);
  return FORMULA_PREFIX_PATTERN.test(raw) ? `'${raw}` : raw;
}

function quoteCsvCell(value: CsvCell): string {
  return `"${sanitizeCsvCell(value).replace(/"/g, '""')}"`;
}

export function buildCsvContent<TRow>({ columns, rows }: CsvExportOptions<TRow>): string {
  const headerLine = columns.map((column) => quoteCsvCell(column.header)).join(",");
  const rowLines = rows.map((row) => columns.map((column) => quoteCsvCell(column.value(row))).join(","));

  return `\uFEFF${[headerLine, ...rowLines].join("\r\n")}\r\n`;
}

export function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export function formatTenantDateForCsvFilename(date = new Date(), timeZone = "Asia/Tokyo"): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(date);
  const year = parts.find((part) => part.type === "year")?.value ?? "0000";
  const month = parts.find((part) => part.type === "month")?.value ?? "00";
  const day = parts.find((part) => part.type === "day")?.value ?? "00";

  return `${year}${month}${day}`;
}
